import { useMemo } from 'react';
import { Empty, Tag, Tooltip } from 'antd';
import { CrownOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import type { OwnershipShare, UboResult } from '@/api/types';

/** UBO / controlling thresholds (FATF beneficial-owner ≥ 25%; OFAC control at ≥ 50%). */
const UBO_THRESHOLD = 0.25;
const CONTROL_THRESHOLD = 0.5;

function pct(fraction: number): string {
  const p = fraction * 100;
  return `${p >= 10 || p === 0 ? p.toFixed(1) : p.toFixed(2)}%`;
}

function OwnerRow({ owner }: { owner: OwnershipShare }) {
  const isUbo = owner.fraction >= UBO_THRESHOLD;
  const isControl = owner.fraction >= CONTROL_THRESHOLD;
  const indent = Math.max(0, (owner.minDepth ?? 1) - 1) * 18;
  const widthPct = Math.max(2, Math.min(100, owner.fraction * 100));
  return (
    <div className={`ol-ubo-row${isUbo ? ' ol-ubo-row--ubo' : ''}`} style={{ marginLeft: indent }}>
      {owner.minDepth != null && owner.minDepth > 1 && <span className="ol-ubo-row__spine" aria-hidden />}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {owner.ownerName ?? owner.ownerId}
          </span>
          {isControl ? (
            <Tag bordered={false} color="error" style={{ fontSize: 9.5, margin: 0 }}>
              <CrownOutlined /> Controlling
            </Tag>
          ) : isUbo ? (
            <Tag bordered={false} color="warning" style={{ fontSize: 9.5, margin: 0 }}>
              <SafetyCertificateOutlined /> UBO
            </Tag>
          ) : null}
          {owner.ownerType && (
            <Tag bordered={false} style={{ fontSize: 9.5, margin: 0 }}>
              {owner.ownerType}
            </Tag>
          )}
        </div>
        {owner.ownerName && (
          <div className="ol-mono" style={{ fontSize: 10, color: 'var(--ol-text-tertiary)', wordBreak: 'break-all' }}>
            {owner.ownerId}
          </div>
        )}
      </div>
      <Tooltip title={`Effective ownership ${pct(owner.fraction)} · reached at depth ${owner.minDepth ?? 1}`}>
        <div className="ol-ubo-row__bar" aria-hidden>
          <div className="ol-ubo-row__fill" style={{ width: `${widthPct}%` }} />
        </div>
      </Tooltip>
      <span className="ol-ubo-pct">{pct(owner.fraction)}</span>
    </div>
  );
}

/**
 * UBO ownership rendered as a clean indented list (DOM, not graph-viz — constraint). Indented by
 * the depth at which each owner is first reached; sorted by effective fraction; UBO / controlling
 * thresholds flagged. Effective fraction = Σ over simple paths of Π of edge weights (server-computed).
 */
export function UboTree({ ubo }: { ubo: UboResult | null }) {
  const owners = useMemo(
    () => (ubo?.owners ? [...ubo.owners].sort((a, b) => b.fraction - a.fraction) : []),
    [ubo],
  );
  if (!ubo || owners.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No ownership edges resolved." />;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {owners.map((o) => (
        <OwnerRow key={`${o.ownerId}-${o.minDepth}`} owner={o} />
      ))}
    </div>
  );
}
